// pages/grant-info.ts — "How to apply" page.
// Uses Bootstrap's native accordion for the FAQ — bootstrap.bundle.min.js
// handles the open/close behaviour, no extra JS needed.

import type { Page, PageContext } from '../router';
import { html, arrowR } from '../util';

const STEPS: Array<[string, string, string, string]> = [
  ['01', 'Check eligibility',
   "Verify your 501(c)(3) status and confirm your project aligns with our funding priorities. We fund community-rooted work in our region; we don't fund individuals, political activity, or proselytizing.",
   'About 2 minutes'],
  ['02', 'Create an account',
   'Sign in with Google. Your account holds drafts and the history of decisions.',
   'About 1 minute'],
  ['03', 'Tell us about your project',
   'A three-step form: project details, supporting documents, a quick review. You can save a draft and come back later.',
   'About 30 minutes'],
  ['04', 'We read it',
   "Two volunteers read every application. We'll message you through the dashboard if anything is unclear. A decision lands in about four weeks.",
   'About 4 weeks'],
];

const REQUIRED_DOCS: Array<[string, string]> = [
  ['Grant proposal', 'A project narrative — PDF, DOCX, MD, or TXT. Two pages is plenty.'],
  ['IRS Determination Letter', 'Your official 501(c)(3) determination. We verify it on submit.'],
];

const HELPFUL_DOCS: Array<[string, string]> = [
  ['Budget', 'A simple line-item budget — even a spreadsheet screenshot is fine.'],
  ['Supporting materials', 'Letters of support, impact reports, photos of past work.'],
  ['External links', 'Google Drive or Dropbox links for anything too large to upload.'],
];

const FAQS: Array<[string, string]> = [
  ['Who is eligible to apply?',
   'Any organization with active 501(c)(3) status that primarily serves communities in our region. We can verify your status from your EIN.'],
  ['What is the grant range?',
   '$500 to $10,000. The average grant is around $2,500. We rarely fund the full ask — be honest about what the project really costs.'],
  ['What documents do I need?',
   'A short project narrative (PDF, DOCX, MD, or TXT) and your IRS Determination Letter. A budget and supporting materials help, but aren\u2019t required.'],
  ['How long does review take?',
   'Around four weeks from submission. We\u2019ll write to you with questions if anything is unclear before then.'],
  ['Can I submit more than one application?',
   "Yes, but we'll only approve one per organization per cycle. Submit your strongest project."],
  ['What happens after I submit?',
   "You'll see status changes in your dashboard. If approved, the money is wired within ten business days of the decision."],
];

function renderSteps(): string {
  return STEPS.map(([num, title, body, time], i) => html`
    <div class="grant-info-step${i === 0 ? ' grant-info-step--first' : ''}">
      <div class="t-small" style="color: var(--primary); font-weight: 500; font-size: 14px;">${num}</div>
      <div>
        <h3 class="t-h3 serif" style="margin: 0;">${title}</h3>
        <p class="t-body" style="margin: 16px 0 0; max-width: 620px; font-size: 16.5px;">${body}</p>
      </div>
      <div class="t-small" style="text-align: right;">${time}</div>
    </div>
  `).join('') + '<div style="border-top: 1px solid var(--rule-2);"></div>';
}

function renderDocList(items: Array<[string, string]>): string {
  return items.map(([t, b]) => html`
    <div>
      <h4 class="t-h4 serif" style="margin: 0; font-size: 18px;">${t}</h4>
      <p class="t-body" style="margin-top: 8px; margin-bottom: 0; font-size: 15px;">${b}</p>
    </div>
  `).join('');
}

function renderFAQs(): string {
  return FAQS.map(([q, a], i) => html`
    <div class="accordion-item">
      <h2 class="accordion-header" id="faqHeading${i}">
        <button class="accordion-button collapsed" type="button"
                data-bs-toggle="collapse" data-bs-target="#faqCollapse${i}"
                aria-expanded="false" aria-controls="faqCollapse${i}">
          ${q}
        </button>
      </h2>
      <div id="faqCollapse${i}" class="accordion-collapse collapse"
           aria-labelledby="faqHeading${i}" data-bs-parent="#faqAccordion">
        <div class="accordion-body">
          <p class="t-body" style="margin: 0; max-width: 680px;">${a}</p>
        </div>
      </div>
    </div>
  `).join('');
}

const GrantInfoPage: Page = {
  render(ctx: PageContext): string {
    // Auth-aware CTA target (mirrors Public.jsx :: GrantInfoPage).
    const beginTarget = !ctx.user
      ? 'login'
      : (ctx.userRole === 'user' ? 'grant-form' : 'admin-dashboard');
    const beginLabel = !ctx.user
      ? 'Begin application'
      : (ctx.userRole === 'user' ? 'Start a new application' : 'Go to dashboard');

    return html`
      <section style="padding: 80px 0 56px;">
        <div class="wrap">
          <div style="max-width: 880px;">
            <div class="eyebrow">How to apply</div>
            <h1 class="t-h1" style="margin: 16px 0 0;">
              Applying for a grant.
            </h1>
            <p class="t-lede" style="margin-top: 32px;">
              We've kept the process intentionally small. Four steps, two required documents, one real person reading the result. About thirty minutes to complete; about four weeks to a decision.
            </p>
          </div>
        </div>
      </section>

      <!-- PROCESS -->
      <section style="padding: 48px 0 96px;">
        <div class="wrap">
          <div class="eyebrow">The process</div>
          <div style="margin-top: 32px;">
            ${{ __raw: renderSteps() }}
          </div>
        </div>
      </section>

      <!-- DOCUMENTS -->
      <section class="divide divide-soft">
        <div class="wrap">
          <div class="eyebrow">What we need from you</div>
          <h2 class="t-h2" style="margin: 16px 0 48px;">Two required documents. The rest is up to you.</h2>

          <div class="grant-info-doc-grid">
            <div class="card" style="padding: 36px 36px 32px;">
              <div class="label" style="color: var(--st-action);">Required</div>
              <h3 class="t-h3 serif" style="margin: 16px 0 24px;">Two documents to submit.</h3>
              <div class="d-flex flex-column" style="gap: 24px;">
                ${{ __raw: renderDocList(REQUIRED_DOCS) }}
              </div>
            </div>
            <div class="card" style="padding: 36px 36px 32px;">
              <div class="label" style="color: var(--primary);">Helpful, not required</div>
              <h3 class="t-h3 serif" style="margin: 16px 0 24px;">Anything else that helps tell the story.</h3>
              <div class="d-flex flex-column" style="gap: 24px;">
                ${{ __raw: renderDocList(HELPFUL_DOCS) }}
              </div>
            </div>
          </div>

          <div class="card grant-info-irs-strip" style="margin-top: 48px;">
            <div class="label" style="color: var(--primary);">501(c)(3) only</div>
            <p class="t-body" style="margin: 0; font-size: 16px;">
              We fund registered tax-exempt organizations only. If you're not sure about your status, the IRS site is the source of truth.
            </p>
            <a class="btn btn-outline-primary" href="https://apps.irs.gov/app/eos/" target="_blank" rel="noopener noreferrer">
              Verify status ${{ __raw: arrowR() }}
            </a>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="divide">
        <div class="wrap" style="max-width: 920px; margin: 0 auto;">
          <div class="eyebrow">FAQ</div>
          <h2 class="t-h2" style="margin: 16px 0 48px;">Questions, before you start.</h2>

          <div class="accordion accordion-flush bff-accordion" id="faqAccordion">
            ${{ __raw: renderFAQs() }}
          </div>

          <div class="grant-info-faq-footer">
            <a class="btn btn-primary" data-nav="${beginTarget}">${beginLabel} ${{ __raw: arrowR() }}</a>
          </div>
        </div>
      </section>
    `;
  },
};

export default GrantInfoPage;
